/* eslint-disable jsx-a11y/media-has-caption */
import {
  ComponentProps,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { match, P } from 'ts-pattern';
import { MediaType } from '~/utils/constants';
import Icon from './Icon';
import { Modal, ModalTitle } from './Modal';

function useMediaType({ src, type }: { src?: string; type?: string }) {
  const [state, setState] = useState<{
    loading: boolean;
    error?: Error;
    mediaType: null | string;
  }>({
    loading: false,
    error: undefined,
    mediaType: type ?? null,
  });

  const fetchMediaType = useCallback((url: string) => {
    setState({
      loading: true,
      error: undefined,
      mediaType: null,
    });

    const errorMsg = `Failed to fetch media type for ${url}`;

    fetch(url, { method: 'HEAD' })
      .then((resp) => {
        if (!resp.ok) {
          throw new Error(errorMsg);
        }
        const contentType = resp.headers.get('Content-Type');

        setState((prev) => ({
          ...prev,
          mediaType: contentType,
        }));
      })
      .catch((error) => {
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error : new Error(errorMsg),
        }));
      })
      .finally(() => {
        setState((prev) => ({
          ...prev,
          loading: false,
        }));
      });
  }, []);

  useEffect(() => {
    if (!type && src) fetchMediaType(src);
  }, [fetchMediaType, type, src]);

  return state;
}

export function MediaFallback({
  className,
  alt,
  type,
}: {
  className?: string;
  alt?: string;
  type?: string | null;
}) {
  const icon = match(type)
    .returnType<ComponentProps<typeof Icon>['icon']>()
    .with(P.string.startsWith('image'), () => 'ImageSolid')
    .with(P.string.startsWith('video'), () => 'FileVideo')
    .with(P.string.startsWith('application/pdf'), () => 'FilePdf')
    .otherwise(() => 'File');

  return (
    <div
      className={twMerge(
        'flex aspect-square h-full w-full items-center justify-center rounded-lg border border-outline bg-white text-gray-500',
        className,
      )}
    >
      <Icon icon={icon} className="size-6 md:size-8" title={alt} />
    </div>
  );
}

function MediaModal({ children }: PropsWithChildren) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="group absolute inset-0 flex cursor-zoom-in items-center justify-center focus:ring-2 focus:ring-primary focus:outline-none"
        onClick={() => setOpen(true)}
      >
        <span className="sr-only">View Full Size</span>
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        className={twMerge(
          'flex max-h-[80svh] w-fit max-w-[90svw] flex-col border-none p-0',
        )}
      >
        <ModalTitle className="sr-only">Expanded media</ModalTitle>
        {children}
      </Modal>
    </>
  );
}

function ImageDisplay({
  src,
  alt,
  expandable,
}: {
  src?: string;
  alt?: string;
  expandable: boolean;
}) {
  const [state, setState] = useState({
    error: false,
    loading: true,
  });

  const showFallback = state.error || state.loading || !src;

  const Img = (
    <img
      className={twMerge(
        'max-h-[80svh] max-w-[90svw]',
        showFallback ? 'invisible' : 'visible',
      )}
      src={src ?? undefined}
      alt={alt}
      onLoad={() => {
        setState({
          error: false,
          loading: false,
        });
      }}
      onError={() => {
        setState({
          error: true,
          loading: false,
        });
      }}
    />
  );

  return (
    <>
      {Img}
      {showFallback ?
        <MediaFallback type="image" />
      : null}
      {expandable && <MediaModal>{Img}</MediaModal>}
    </>
  );
}

function VideoDisplay({
  src,
  alt,
  expandable,
}: {
  src?: string;
  alt?: string;
  expandable: boolean;
}) {
  const Fallback = (
    <MediaFallback type="video" alt="Video content not supported" />
  );
  return (
    <>
      <video src={src} aria-label={alt}>
        {Fallback}
      </video>
      {expandable && (
        <MediaModal>
          <video
            controls
            src={src}
            aria-label={alt}
            className="max-h-[80svh] max-w-[90svw]"
          >
            {Fallback}
          </video>
        </MediaModal>
      )}
    </>
  );
}

/**
 * PDF Display Component - Display a PDF icon for the thumbnail
 * and if the PDF is not a blob URL, it will allow to view the PDF in a modal.
 * The blob URL will cause a SecurityError in the iframe.
 */
function PDFDisplay({
  src,
  alt,
  expandable,
}: {
  src?: string;
  alt?: string;
  expandable: boolean;
}) {
  return (
    <>
      <MediaFallback type="application/pdf" alt={alt} />
      {!src?.startsWith('blob:') && expandable && (
        <MediaModal>
          <iframe
            title={alt ?? 'PDF Preview'}
            loading="lazy"
            src={src}
            // the pdf previewer needs a fixed width to render nicely
            className="aspect-[3/4.25] min-w-[90svw] overflow-hidden border-none"
          />
        </MediaModal>
      )}
    </>
  );
}

type MediaDisplayProps = {
  alt?: string;
  src?: string | null;
  type?: string;
  className?: string;
  expandable?: boolean;
};

export default function MediaDisplay({
  type,
  className,
  alt,
  expandable = false,
  ...props
}: MediaDisplayProps) {
  const src = props.src ?? undefined;

  const mediaTypeQuery = useMediaType({ src, type });

  const display = match(mediaTypeQuery)
    .with({ loading: true }, () => (
      <MediaFallback type="loading" alt="Loading media..." />
    ))
    .with({ mediaType: P.string.startsWith(MediaType.image) }, () => (
      <ImageDisplay src={src} alt={alt} expandable={expandable} />
    ))
    .with({ mediaType: P.string.startsWith(MediaType.video) }, () => (
      <VideoDisplay src={src} alt={alt} expandable={expandable} />
    ))
    .with({ mediaType: `${MediaType.application}/pdf` }, () => (
      <PDFDisplay src={src} alt={alt} expandable={expandable} />
    ))
    .otherwise(({ mediaType }) => (
      <MediaFallback type={mediaType} alt="Unsupported Media Type" />
    ));

  return (
    <div
      className={twMerge(
        'relative flex aspect-square items-center justify-center rounded-lg border border-outline object-cover *:size-full *:rounded-[7px] *:border-none *:object-contain',
        className,
      )}
    >
      {display}
    </div>
  );
}
